/**
 * Auth API — login, impersonation, me.
 */

import { apiFetch, getToken, setToken, getCachedUser, setCachedUser } from './core'

export async function apiLogin(login, password) {
    const data = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ login, password }),
    })
    setToken(data.access_token)
    setCachedUser(data.user)
    return data
}

/**
 * Self-service password change for the logged-in user (old → new).
 * Backend verifies the current password and enforces a minimal policy.
 */
export async function apiChangePassword(currentPassword, newPassword) {
    return apiFetch('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    })
}

// ─────────────────────────────────────────────────────────────────
// Вход через Telegram из обычного браузера (OIDC, ADR-057)
// ─────────────────────────────────────────────────────────────────

// Секрет обмена живёт в sessionStorage, а НЕ в localStorage и не в адресе
// страницы: он привязывает завершение входа к этой вкладке. Именно поэтому
// подсунутая злоумышленником ссылка с чужими code+state не сработает —
// записать свой секрет в чужой sessionStorage он не может.
const TELEGRAM_LOGIN_SECRET_KEY = 'onboardix_telegram_login_secret'

/** Включён ли вход через Telegram — до настройки в BotFather кнопки быть не должно. */
export async function apiTelegramLoginConfig() {
    return apiFetch('/auth/telegram/login-config')
}

/** Начать вход: получить адрес Telegram и запомнить секрет этой вкладки. */
export async function apiTelegramLoginStart() {
    const data = await apiFetch('/auth/telegram/oidc/start', { method: 'POST' })
    sessionStorage.setItem(TELEGRAM_LOGIN_SECRET_KEY, data.login_secret)
    return data
}

/**
 * Завершить вход по коду из адреса возврата.
 * Секрет берётся ТОЛЬКО из sessionStorage — никогда из адресной строки,
 * иначе привязка к вкладке теряет смысл.
 */
export async function apiTelegramLoginFinish(code, state) {
    const loginSecret = sessionStorage.getItem(TELEGRAM_LOGIN_SECRET_KEY)
    if (!loginSecret) {
        throw new Error('Вход начат в другой вкладке или браузере. Откройте страницу входа и попробуйте снова.')
    }
    try {
        const data = await apiFetch('/auth/telegram/oidc/callback', {
            method: 'POST',
            body: JSON.stringify({ code, state, login_secret: loginSecret }),
        })
        setToken(data.access_token)
        setCachedUser(data.user)
        return data
    } finally {
        // Секрет одноразовый в любом исходе: и после успеха, и после отказа.
        sessionStorage.removeItem(TELEGRAM_LOGIN_SECRET_KEY)
    }
}

// ─────────────────────────────────────────────────────────────────
// Запасной вход через бота по ссылке (ADR-058)
// ─────────────────────────────────────────────────────────────────

// Тот же принцип, что и у OIDC: секрет заявки живёт в sessionStorage этой
// вкладки. Идентификатор заявки уезжает в Telegram и виден в истории чата —
// будь он единственным ключом, сессию забрал бы любой, кто его увидел.
const TELEGRAM_BOT_LOGIN_SECRET_KEY = 'onboardix_telegram_bot_login_secret'

/** Выпустить заявку: вернуть ссылку на бота и код подтверждения. */
export async function apiTelegramBotLoginStart() {
    const data = await apiFetch('/auth/telegram/bot/start', { method: 'POST' })
    sessionStorage.setItem(TELEGRAM_BOT_LOGIN_SECRET_KEY, data.login_secret)
    return data
}

/**
 * Узнать судьбу заявки. Возвращает `{ status }`, а при `approved` — ещё и
 * готовую сессию, которая тут же кладётся в хранилище.
 * Терминальный ответ гасит секрет: заявка одноразовая по обе стороны.
 */
export async function apiTelegramBotLoginPoll(requestId) {
    const loginSecret = sessionStorage.getItem(TELEGRAM_BOT_LOGIN_SECRET_KEY)
    if (!loginSecret) {
        return { status: 'expired' }
    }
    const data = await apiFetch('/auth/telegram/bot/poll', {
        method: 'POST',
        body: JSON.stringify({ request_id: requestId, login_secret: loginSecret }),
    })
    if (data.status === 'approved') {
        setToken(data.access_token)
        setCachedUser(data.user)
    }
    if (data.status !== 'pending') {
        sessionStorage.removeItem(TELEGRAM_BOT_LOGIN_SECRET_KEY)
    }
    return data
}

/** Забыть заявку, если человек закрыл окно ожидания сам. */
export function forgetTelegramBotLogin() {
    sessionStorage.removeItem(TELEGRAM_BOT_LOGIN_SECRET_KEY)
}

/**
 * Login via Telegram Mini App initData — Phase 6 PR 6.1.
 * Backend validates HMAC against TELEGRAM_BOT_TOKEN, finds Employee linked
 * through previous `/telegram-link/begin` flow, returns the same JWT shape
 * as `/auth/login` (HARD GATE 1).
 *
 * On 403 `telegram_not_linked` — caller should show «Свяжите Telegram в
 * личном кабинете» с deep-link на /telegram-link/begin.
 */
export async function apiTelegramWebAppLogin(initData) {
    const data = await apiFetch('/auth/telegram-webapp', {
        method: 'POST',
        body: JSON.stringify({ init_data: initData }),
    })
    setToken(data.access_token)
    setCachedUser(data.user)
    return data
}

export async function apiImpersonationTargets() {
    return apiFetch('/auth/impersonation-targets')
}

export async function apiImpersonate(userId) {
    const data = await apiFetch(`/auth/impersonate/${userId}`, { method: 'POST' })
    const oldToken = getToken()
    if (oldToken) localStorage.setItem('onboardix_original_token', oldToken)
    setToken(data.access_token)
    setCachedUser(data.user)
    return data
}

/**
 * Test accounts the current user may "view as" (non-admin, by binding).
 * Returns `{ targets: [{ id, login, full_name, role }] }`. Empty for users
 * without bindings — the topbar "Смотреть как" entry stays hidden.
 */
export async function apiViewAsTargets() {
    return apiFetch('/auth/view-as/targets')
}

/**
 * Start a "view as" session as a bound test account. Mirrors apiImpersonate:
 * saves the original token so stopImpersonating() can restore it. Authorization
 * is enforced server-side by the binding, not by admin role.
 */
export async function apiViewAs(userId) {
    const data = await apiFetch(`/auth/view-as/${encodeURIComponent(userId)}`, { method: 'POST' })
    const oldToken = getToken()
    if (oldToken) localStorage.setItem('onboardix_original_token', oldToken)
    setToken(data.access_token)
    setCachedUser(data.user)
    return data
}

export async function apiStopImpersonating() {
    const originalToken = localStorage.getItem('onboardix_original_token')
    if (!originalToken) {
        throw new Error('Не найдена оригинальная сессия')
    }

    // Сохранённый токен снимаем ТОЛЬКО после удачного возврата.
    //
    // Раньше `removeItem` стоял до `apiGetMe()`, и первая же неудача стирала
    // единственный путь назад: следующий клик по «выйти из-под пользователя»
    // отвечал «Не найдена оригинальная сессия» — и так навсегда, потому что
    // баннер держится на наличии этого же ключа и обратно уже не появлялся бы.
    // Человек оставался заперт в чужой учётке (тикет e262470b).
    const impersonatedToken = getToken()
    setToken(originalToken)

    let data
    try {
        data = await apiGetMe()
    } catch (err) {
        // `apiFetch` на 401 сам снимает активный токен и шлёт `auth:logout`.
        // Пустой токен здесь — признак того, что сервер отверг именно
        // оригинальную сессию: возвращаться некуда, держать ключ бессмысленно,
        // и честный исход — обычный вход под своей учётной записью.
        if (getToken() === null) {
            localStorage.removeItem('onboardix_original_token')
            throw new Error('Оригинальная сессия истекла — войдите заново под своей учётной записью')
        }
        // Иначе сбой временный (сеть, 5xx). Возвращаем состояние до попытки:
        // ключ на месте, значит повтор осмыслен.
        if (impersonatedToken) setToken(impersonatedToken)
        throw err
    }

    localStorage.removeItem('onboardix_original_token')
    setCachedUser(data)
    return data
}

export async function apiGetMe() {
    return apiFetch('/auth/me')
}

export async function apiGetCapabilities() {
    return apiFetch('/auth/capabilities')
}

export async function apiGetRolePermissions() {
    return apiFetch('/auth/role-permissions')
}

export async function apiUpdateRolePermissions(role, permissions) {
    return apiFetch(`/auth/role-permissions/${encodeURIComponent(role)}`, {
        method: 'PATCH',
        body: JSON.stringify({ permissions }),
    })
}

// `base_role` — роль-прототип: чьи двери открывает эта роль (#5633). Пустая
// строка означает «без прототипа»; на создании она уезжает как есть, на правке
// снимает ранее выбранный прототип.
export async function apiCreateRole({ code, label, icon = '', color = '', desc = '', permissions = [], base_role = '' }) {
    return apiFetch('/auth/roles', {
        method: 'POST',
        body: JSON.stringify({ code, label, icon, color, desc, permissions, base_role }),
    })
}

// Поля, которых нет в объекте, в тело не попадают: ручка правит только
// присланное, а `null`/отсутствие для неё значат «не трогать».
export async function apiUpdateRoleMeta(role, { label, icon, color, desc, base_role }) {
    return apiFetch(`/auth/roles/${encodeURIComponent(role)}`, {
        method: 'PATCH',
        body: JSON.stringify({ label, icon, color, desc, base_role }),
    })
}

export async function apiDeleteRole(role, { reassignTo } = {}) {
    const qs = reassignTo ? `?reassign_to=${encodeURIComponent(reassignTo)}` : ''
    return apiFetch(`/auth/roles/${encodeURIComponent(role)}${qs}`, {
        method: 'DELETE',
    })
}

/**
 * Bulk-удаление custom-ролей, дублирующих built-in по label.
 * `dryRun=true` — план без изменений; иначе reassign + delete каждого дубля.
 */
export async function apiCleanupDuplicateRoles({ dryRun = false } = {}) {
    const qs = dryRun ? '?dry_run=true' : ''
    return apiFetch(`/auth/roles/cleanup-duplicates${qs}`, { method: 'POST' })
}

/**
 * Suggest sidebar module keys for a role based on its current permissions.
 * Returns `{ role, modules: [string] }`. Used by RBAC UI to autofill
 * `extra_modules` when an admin assigns a custom role to a user.
 */
export async function apiGetRoleSuggestedModules(role) {
    return apiFetch(`/auth/roles/${encodeURIComponent(role)}/suggested-modules`)
}

/**
 * Replace user's `extra_modules` with the canonical set derived from role
 * permissions (PERMISSION_TO_MODULES mapping). Cleans accumulated stale
 * entries from previous role assignments. Returns updated UserOut.
 */
export async function apiSyncUserModulesFromRole(userId) {
    return apiFetch(`/users/${encodeURIComponent(userId)}/sync-modules-from-role`, {
        method: 'POST',
    })
}

export async function apiGetUserPermissions() {
    return apiFetch('/auth/user-permissions')
}

export async function apiUpdateUserPermissions(userId, allow_permissions, deny_permissions) {
    return apiFetch(`/auth/user-permissions/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ allow_permissions, deny_permissions }),
    })
}

export async function apiSyncAccounts() {
    return apiFetch('/auth/sync-accounts', { method: 'POST' })
}
